/**
 * Pushback Loop Editor - configures a pushback node to push back once per element of a
 * list in an upstream node's output (e.g. an agent returning {"pushbacks": [{...}, ...]}).
 *
 * Mirrors PushbackLoop / ElementValueBinding (raiqon/schemas/workflow.py): the list comes
 * from a step_output binding, and each element's target field / value / operation is read
 * from a field of that element or set to a fixed value.
 */

import React from 'react';
import type { PipelineDefinition } from '../types/pipeline';
import { listItemSchema, pathType } from '../utils/outputPath';
import { OutputPathPicker, useAgentOutputSchema } from './OutputPathPicker';
import { getUpstreamNodes } from './ValueBindingEditor';

export const PUSHBACK_OPERATIONS = ['APPEND', 'DELETE', 'OVERRIDE'] as const;

export interface ElementValueBindingConfig {
  source: 'element_field' | 'literal';
  element_field?: string;
  literal_value?: string;
}

export interface PushbackLoopConfig {
  // Always a step_output binding - the list's length is only known once that node has run.
  items_binding: { source: 'step_output'; upstream_step_reference?: string; output_field?: string };
  target_field: ElementValueBindingConfig;
  value: ElementValueBindingConfig;
  // Unset = every element uses the node's operation.
  operation?: ElementValueBindingConfig;
}

export function emptyPushbackLoop(): PushbackLoopConfig {
  return {
    items_binding: { source: 'step_output', upstream_step_reference: '', output_field: '' },
    target_field: { source: 'element_field', element_field: '' },
    value: { source: 'element_field', element_field: '' },
  };
}

export function isElementBindingComplete(binding: ElementValueBindingConfig | undefined): boolean {
  if (!binding) return false;
  return binding.source === 'element_field'
    ? (binding.element_field ?? '').trim() !== ''
    : (binding.literal_value ?? '').trim() !== '';
}

export function isPushbackLoopComplete(loop: PushbackLoopConfig): boolean {
  return (
    (loop.items_binding.upstream_step_reference ?? '').trim() !== '' &&
    (loop.items_binding.output_field ?? '').trim() !== '' &&
    isElementBindingComplete(loop.target_field) &&
    isElementBindingComplete(loop.value) &&
    (loop.operation === undefined || isElementBindingComplete(loop.operation))
  );
}

// Strip fields that don't belong to the selected source - ElementValueBinding is strict.
export function normalizeElementBinding(binding: ElementValueBindingConfig): ElementValueBindingConfig {
  return binding.source === 'element_field'
    ? { source: 'element_field', element_field: (binding.element_field ?? '').trim() }
    : { source: 'literal', literal_value: (binding.literal_value ?? '').trim() };
}

type JsonSchema = Record<string, any>;

interface ElementBindingRowProps {
  label: string;
  binding: ElementValueBindingConfig;
  onChange: (binding: ElementValueBindingConfig) => void;
  elementSchema: JsonSchema | undefined;
  literalLabel: string;
  // Fixed-value choices; free text when omitted.
  literalOptions?: readonly string[];
  literalPlaceholder?: string;
}

const ElementBindingRow: React.FC<ElementBindingRowProps> = ({
  label,
  binding,
  onChange,
  elementSchema,
  literalLabel,
  literalOptions,
  literalPlaceholder,
}) => (
  <div className="condition-row">
    <label>{label}</label>
    <select
      className="condition-input"
      value={binding.source}
      onChange={(e) => onChange({ ...binding, source: e.target.value as ElementValueBindingConfig['source'] })}
    >
      <option value="element_field">Field of each list item</option>
      <option value="literal">{literalLabel}</option>
    </select>
    {binding.source === 'element_field' ? (
      <OutputPathPicker
        value={binding.element_field ?? ''}
        onChange={(element_field) => onChange({ ...binding, element_field })}
        schema={elementSchema}
        includeRawResponse={false}
        placeholder="Field of each list item, e.g. target_field"
      />
    ) : literalOptions ? (
      <select
        className="condition-input"
        value={binding.literal_value ?? ''}
        onChange={(e) => onChange({ ...binding, literal_value: e.target.value })}
      >
        <option value="">Select...</option>
        {literalOptions.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    ) : (
      <input
        type="text"
        className="condition-input"
        value={binding.literal_value ?? ''}
        onChange={(e) => onChange({ ...binding, literal_value: e.target.value })}
        placeholder={literalPlaceholder}
      />
    )}
  </div>
);

interface PushbackLoopEditorProps {
  loop: PushbackLoopConfig;
  onChange: (loop: PushbackLoopConfig) => void;
  // The node-level operation, used for elements without an operation of their own.
  operation: (typeof PUSHBACK_OPERATIONS)[number];
  onOperationChange: (operation: (typeof PUSHBACK_OPERATIONS)[number]) => void;
  pipeline: PipelineDefinition | null;
  nodeId: string;
}

export const PushbackLoopEditor: React.FC<PushbackLoopEditorProps> = ({
  loop,
  onChange,
  operation,
  onOperationChange,
  pipeline,
  nodeId,
}) => {
  const upstreamNodes = getUpstreamNodes(pipeline, nodeId);
  const selectedUpstream = pipeline?.nodes.find((n) => n.id === loop.items_binding.upstream_step_reference);
  const { schema: upstreamSchema, loading } = useAgentOutputSchema(selectedUpstream?.agent_id);
  const listPath = loop.items_binding.output_field ?? '';
  const elementSchema = listItemSchema(listPath, upstreamSchema);
  const listPathType = upstreamSchema && listPath ? pathType(listPath, upstreamSchema) : 'unknown';

  // Keep an already-saved upstream reference selectable even if its edge was removed.
  const upstreamOptions =
    selectedUpstream && !upstreamNodes.some((n) => n.id === selectedUpstream.id)
      ? [...upstreamNodes, selectedUpstream]
      : upstreamNodes;

  const setItems = (items_binding: PushbackLoopConfig['items_binding']) => onChange({ ...loop, items_binding });
  const perElementOperation = loop.operation !== undefined;

  return (
    <>
      <div className="config-section">
        <h4>List of pushbacks</h4>
        <p className="field-hint">The upstream output field holding the list - one pushback is created per list item.</p>
        <div className="condition-row">
          <label>Upstream node</label>
          <select
            className="condition-input"
            value={loop.items_binding.upstream_step_reference ?? ''}
            onChange={(e) => setItems({ source: 'step_output', upstream_step_reference: e.target.value, output_field: '' })}
          >
            <option value="">{upstreamOptions.length ? 'Select an upstream node...' : 'Connect an upstream node first'}</option>
            {upstreamOptions.map((n) => (
              <option key={n.id} value={n.id}>
                {n.label || n.id}
              </option>
            ))}
          </select>
        </div>
        {loop.items_binding.upstream_step_reference && (
          <div className="condition-row">
            <label>List field</label>
            <OutputPathPicker
              value={listPath}
              onChange={(output_field) => setItems({ ...loop.items_binding, output_field })}
              schema={upstreamSchema}
              loading={loading}
              includeRawResponse={false}
              placeholder="List field, e.g. pushbacks"
            />
            {listPathType !== 'unknown' && listPathType !== 'array' && (
              <p className="field-error">This field is not a list in the upstream agent's output schema.</p>
            )}
          </div>
        )}
      </div>

      <div className="config-section">
        <h4>For each list item</h4>
        <ElementBindingRow
          label="Target Field"
          binding={loop.target_field}
          onChange={(target_field) => onChange({ ...loop, target_field })}
          elementSchema={elementSchema}
          literalLabel="Same field for every item"
          literalPlaceholder="Codebeamer field name"
        />
        <ElementBindingRow
          label="Value"
          binding={loop.value}
          onChange={(value) => onChange({ ...loop, value })}
          elementSchema={elementSchema}
          literalLabel="Same value for every item"
          literalPlaceholder="Value"
        />
        <div className="condition-row">
          <label>Operation</label>
          <select
            className="condition-input"
            value={perElementOperation ? 'element_field' : 'fixed'}
            onChange={(e) =>
              onChange({
                ...loop,
                operation: e.target.value === 'element_field' ? { source: 'element_field', element_field: '' } : undefined,
              })
            }
          >
            <option value="fixed">Same operation for every item</option>
            <option value="element_field">Field of each list item</option>
          </select>
          {perElementOperation ? (
            <>
              <OutputPathPicker
                value={loop.operation?.element_field ?? ''}
                onChange={(element_field) => onChange({ ...loop, operation: { source: 'element_field', element_field } })}
                schema={elementSchema}
                includeRawResponse={false}
                placeholder="Field of each list item, e.g. operation"
              />
              <p className="field-hint">
                Items without an operation use {operation}; an unknown operation fails that item.
              </p>
            </>
          ) : null}
          <select
            className="condition-input"
            value={operation}
            onChange={(e) => onOperationChange(e.target.value as (typeof PUSHBACK_OPERATIONS)[number])}
            aria-label={perElementOperation ? 'Fallback operation' : 'Operation'}
          >
            {PUSHBACK_OPERATIONS.map((op) => (
              <option key={op} value={op}>
                {perElementOperation ? `Fallback: ${op}` : op}
              </option>
            ))}
          </select>
        </div>
      </div>
    </>
  );
};
